import React from "react";
import { PlanDetails } from "@swiggy-private/cart-types";

import { getCloudinaryUrl, ScreenReaderOnly, isEmpty, replaceText } from "@swiggy-private/core-ui";
import { isOneFlagship, isOneLite, isSwiggyBlack } from "./index";

import { TITLE_LOGO_CONFIG, A11Y_LOGO_REPLACEMENTS, ONE_BLACK_LOGO_CONFIG } from "../constants";

import { PLACEHOLDERS } from "@super-ui/constants";
import { imageConfigFormatter } from "@super-ui/helpers/String";
import { ImageBuilderArguments } from "../types";

import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

import { InlineImageSpan } from "../SwiggyOneWidgetDLSWrapper.styles";
import styles from "./styles/nodeBuilders.scss";
import { CART_UPSELL_LOGO_CONFIG } from "@super-ui/components/cart-upsell-nudge/constants";

import { ITextPropsContainer } from "@swiggy-private/dls-web-core/dist/components/Text/types";
import VerticalScrollContainer from "../components/vertical-scroll-widget";

import { ITypographyV2 } from "@swiggy-private/dls-web-common/dist/components/types.internal";

const FALLBACK_TITLE = {
    VARIANT: "V2.body1.neutral.bold" as keyof ITypographyV2,
    HEIGHT: 21,
};

const getLogoConfig = (planDetails: PlanDetails, config?: ITextPropsContainer) => {
    const logoConfig = isEmpty(config) ? TITLE_LOGO_CONFIG : CART_UPSELL_LOGO_CONFIG;

    if (isOneLite(planDetails)) {
        // https://media-assets.swiggy.com/swiggy/image/upload/fl_lossy,f_auto,q_auto/Swiggy%20One%20Lite/One_lite_Horizontal
        return logoConfig.LITE;
    } else if (isOneFlagship(planDetails)) {
        // https://media-assets.swiggy.com/swiggy/image/upload/fl_lossy,f_auto,q_auto/portal/m/one-logo-padded-for-cart.png
        return logoConfig.PLUS;
    } else if (isSwiggyBlack(planDetails)) {
        const blackLogoConfig = isEmpty(config) ? ONE_BLACK_LOGO_CONFIG : CART_UPSELL_LOGO_CONFIG.BLACK;
        return blackLogoConfig;
    }
};

const getA11yText = (planDetails: PlanDetails) => {
    if (isOneLite(planDetails)) {
        return A11Y_LOGO_REPLACEMENTS.LITE;
    } else if (isOneFlagship(planDetails)) {
        return A11Y_LOGO_REPLACEMENTS.ONE;
    } else if (isSwiggyBlack(planDetails)) {
        return A11Y_LOGO_REPLACEMENTS.BLACK;
    }
};

export const inlineImageBuilder = ({ width, height, cloudinaryId, imageVerticalAlign }: ImageBuilderArguments) => {
    if (isEmpty(cloudinaryId)) return null;
    return (
        <>
            <InlineImageSpan width={width} height={height} imageVerticalAlign={imageVerticalAlign}>
                <Image cloudinaryId={cloudinaryId} fit="contain" cloudinaryHeight={60} />
            </InlineImageSpan>
        </>
    );
};
export const titleNodeBuilder = (
    displayText: string,
    logo: string,
    eligiblePlanDetails: PlanDetails,
    config?: ITextPropsContainer,
) => {
    const logoWidthHeightConfig = getLogoConfig(eligiblePlanDetails, config);
    const isOneLiteLogo = isOneLite(eligiblePlanDetails);
    const isBlack = isSwiggyBlack(eligiblePlanDetails);

    if (isEmpty(displayText) || !logoWidthHeightConfig) return null;

    const logoA11YText = getA11yText(eligiblePlanDetails);
    if (!displayText.includes(PLACEHOLDERS.LOGO)) {
        // if displayText doesn't have any {{logo}} placeholder add it to start as a fallback
        displayText = `${PLACEHOLDERS.LOGO} ${displayText}`;
    }

    // assumes only 1 {{logo}} placeholder in displayText
    const displayTextSplit = displayText.split(PLACEHOLDERS.LOGO);
    const [logoPretext, logoPostText] = displayTextSplit;

    // note: empty spaces inserted in the pretext and posttext
    const finalPreText = `${logoPretext?.replace(PLACEHOLDERS.LOGO, "")?.trim()} `;
    const finalPostText = ` ${logoPostText?.replace(PLACEHOLDERS.LOGO, "")?.trim()}`;
    const a11yText = `${finalPreText}${logoA11YText}${finalPostText}`.trim();

    let imageVerticalAlign = isOneLiteLogo || isBlack ? "text-top" : "baseline";

    const textColor = isBlack ? "primary_One_Blck" : "swiggy_One";

    if (isEmpty(config)) {
        config = { variant: "V2.body1.neutral.bold", color: textColor };
        imageVerticalAlign = "sub";
    }

    if (isSwiggyBlack(eligiblePlanDetails)) {
        imageVerticalAlign = "sub";
    }

    // Omit the ref property from config
    const { ref, ...configWithoutRef } = config;

    return (
        <>
            <ScreenReaderOnly text={a11yText} />
            <Text aria-hidden {...configWithoutRef}>
                {finalPreText}
                {inlineImageBuilder({ ...logoWidthHeightConfig, cloudinaryId: logo, imageVerticalAlign })}
                {finalPostText}
            </Text>
        </>
    );
};

export const descriptionNodeBuilder = (subtitles: string[], isOneLite = false) => {
    if (isEmpty(subtitles) || isEmpty(subtitles?.[0])) return null;
    const descriptionText = subtitles?.[0];
    let htmlString, a11yText;

    // replace all bold tags with <span> tags
    htmlString = descriptionText.split(PLACEHOLDERS.BOLD_START).join('<span style="font-weight: 600;">');
    htmlString = htmlString.split(PLACEHOLDERS.BOLD_END).join("</span>");
    // a11y text string should not have any <span> or [[ ]] tags
    a11yText = descriptionText.split(PLACEHOLDERS.BOLD_START).join("");
    a11yText = a11yText.split(PLACEHOLDERS.BOLD_END).join("");

    // this code blocks works for {{img|wpx|hpx|cloudinaryId}} image insertion and replacements
    if (htmlString.includes(PLACEHOLDERS.INLINE_IMG_START) && htmlString.includes(PLACEHOLDERS.INLINE_IMG_END)) {
        // extract all images from descriptionText aand replace them with inline HTML images
        for (let i = 0; i < htmlString.length; ) {
            const imageStartIdx = htmlString.indexOf(PLACEHOLDERS.INLINE_IMG_START, i);
            const imageEndIdx = htmlString.indexOf(PLACEHOLDERS.INLINE_IMG_END) + PLACEHOLDERS.INLINE_IMG_END.length;

            // safety block to prevent issue in case of malformed string
            if (imageStartIdx === -1 || imageEndIdx === -1 || imageEndIdx < imageStartIdx) {
                break;
            }

            const imageString = htmlString.substring(imageStartIdx, imageEndIdx);

            const imageConfig = imageConfigFormatter(
                imageString?.replace(PLACEHOLDERS.INLINE_IMG_START, "").replace(PLACEHOLDERS.INLINE_IMG_END, ""),
            );

            let imageHTML = "";
            // invalid config received
            if (isEmpty(imageConfig)) {
                imageHTML = "";
            } else {
                // had to do height/width styling inline due to these being config driven and scss doesn't intake dynamic values
                imageHTML = `<span class=${styles.imgContainer} style="width: ${imageConfig.width}; height: ${
                    imageConfig.height
                };"><img src=${getCloudinaryUrl(imageConfig.cloudinaryId)}></span>`;
            }

            htmlString = htmlString.replace(imageString, imageHTML);
            // a11y string should just callout One/OneLite instead of the image
            a11yText = a11yText.replace(
                imageString,
                isOneLite ? A11Y_LOGO_REPLACEMENTS.LITE : A11Y_LOGO_REPLACEMENTS.ONE,
            );
            i = Math.max(i + 1, imageEndIdx);
        }
    }

    return (
        <>
            <ScreenReaderOnly text={a11yText} />
            <Text
                as="span"
                variant="V2.body2.neutral.regular"
                color="text_Highest_Emphasis"
                dangerouslySetInnerHTML={{ __html: htmlString }}
                aria-hidden
            />
        </>
    );
};

export const animatedTitleNodeBuilder = ({
    displayText,
    logo,
    eligiblePlanDetails,
    config,
    titleHeight,
}: {
    displayText: string;
    logo: string;
    eligiblePlanDetails: PlanDetails;
    config: ITextPropsContainer;
    titleHeight: number;
}) => {
    const logoWidthHeightConfig = getLogoConfig(eligiblePlanDetails, config);
    const isOneLiteLogo = isOneLite(eligiblePlanDetails);
    const isBlack = isSwiggyBlack(eligiblePlanDetails);

    if (isEmpty(displayText) || !logoWidthHeightConfig) return null;

    const hasLogoPlaceholder = displayText.includes(PLACEHOLDERS.LOGO);
    const displayTextSplit = displayText.split(PLACEHOLDERS.ANIMATE_IDENTIFIER);

    let imageVerticalAlign = isOneLiteLogo || isBlack ? "text-top" : "baseline";

    const textColor = isBlack ? "primary_One_Blck" : "swiggy_One";

    if (isEmpty(config)) {
        config = {
            variant: FALLBACK_TITLE.VARIANT,
            color: textColor,
        };
        titleHeight = FALLBACK_TITLE.HEIGHT;
        imageVerticalAlign = "sub";
    }

    if (isSwiggyBlack(eligiblePlanDetails)) {
        imageVerticalAlign = "sub";
    }

    const totalTitles = displayTextSplit.length;
    const animationTexts = [displayTextSplit[totalTitles - 1], ...displayTextSplit, displayTextSplit[0]];

    // Omit the ref property from config
    const { ref, ...configWithoutRef } = config;

    const logoStyle = isOneLiteLogo ? styles.imgContainerOneLiteLogo : styles.imgContainerOneLogo;

    return (
        <>
            {!hasLogoPlaceholder &&
                inlineImageBuilder({ ...logoWidthHeightConfig, cloudinaryId: logo, imageVerticalAlign })}
            <VerticalScrollContainer
                scrollLengthPerUnit={titleHeight}
                carouselHeight={titleHeight}
                totalCard={totalTitles}
            >
                {animationTexts.map((title, index) => (
                    <Text key={index} aria-hidden maxLines={1} {...configWithoutRef}>
                        {/* Replace LOGO placeholder with {{img|logo}} format that replaceText() expects
                            replaceText() looks for {{img|...}} pattern to convert to <img> tags */}
                        {hasLogoPlaceholder
                            ? replaceText(title.replace(PLACEHOLDERS.LOGO, `{{img|${logo}}}`), logoStyle)
                            : title}
                    </Text>
                ))}
            </VerticalScrollContainer>
        </>
    );
};
